import { observable, computed, action } from 'mobx';
import { AppManager, Variables, client, routerStore } from 'core';
import { getQueryDefinition } from '@apollo/client/utilities';

import { FilterInput } from '../actions/filterModels';

export const requestState = {
  RECONFIGURING: 'reconfiguring',
  LOADING_NEW_PAGE: 'loadingNew',
};

export class PaginationOffsetManager extends AppManager {
  static registryName = 'PaginationOffsetManager';
  /* This could and should be implemented as a Query and 'Field' Result Object, with 'page caching' */
  disposers = [];

  @observable paginationDirection = 'forward';
  @observable requestState = requestState.LOADING_NEW_PAGE;
  @observable batchSize = 10;
  @observable total = 0;
  @observable nodes = [];
  @observable filters = {};
  @observable selectedRows = new Set([]);
  @observable.ref query = null;

  constructor(parent, initial = {}, options) {
    super(parent, initial, options);
    this.InitialiseFilters();

    this.variables = new Variables({
      // move pageInput to the PaginationAppManager?
      first: () => this.batchSize,
      offset: () => {
        // zero the offset if we are adding a filter or sort
        if (this.requestState === requestState.LOADING_NEW_PAGE) {
          return (this.pageNumber - 1) * this.batchSize;
        }
        return 0;
      },
      sortField: () =>
        routerStore.params.get('sort') || this.constructor.defaultSort,
      sortOrder: () =>
        routerStore.params.get('order') || this.constructor.defaultOrder,
      filters: () => this.filtersApiInput,
    });

    this.variables.onChange(
      ['sortOrder', 'sortField', 'filters'],
      // We are reconfiguring the Connection whenever sortOrder, sortField, of filtersInput changes.
      () => {
        this.reConfigure();
      },
      { fireImmediately: false },
    );
  }

  dispose() {
    super.dispose();
    this.disposeFilters();
  }

  @computed get pageNumber() {
    return parseInt(routerStore.params.get('p'), 10) || 1;
  }

  @computed get noDataAfterLoading() {
    return !this.loading && this.total === 0;
  }

  initialiseQuery(Query, variables, headers = null) {
    this.validRouteName = routerStore.route.name;
    if (this.query) {
      this.query.disposer();
      if (Query !== this.query.constructor) {
        // Query and the this.query.constructor are different classes but we presume
        // that they execute the same query call

        // its a new 'mode' so clear the cache
        const ast = Query.conf.GQL;
        const fieldName =
          getQueryDefinition(ast)?.selectionSet?.selections?.[0]?.name?.value;
        // fieldName is the API query name
        // e.g. "requestForQuote", "shipmentQuotes"
        if (fieldName) {
          client.cache.evict({ id: 'ROOT_QUERY', fieldName });
          client.cache.gc();

          this.nodes = [];
          this.total = 0;
        }
      }
      this.reConfigure(/* doNotQuery */ true); // remove ?p= param
    }
    this.variables = variables;
    // Cancel existing requests, to avoid race conditions on the queries.  if the config variable has changed but not if we are paginating.
    this.query = new Query(this, {
      variables: this.variables,
      onSysErr: this.PageLoadErr,
      abortOnRerequest: true,
      headers,
    });

    this.query.process(this.process, this.SetPage);
    // for some bizarre reason this triggers two requests when used with query de-duplication 'false'
    this.query.query();
  }

  @action PageLoadErr = () => {
    // alert the error?
  };

  @computed get loading() {
    if (this.query?.loading) return true;
    return false;
  }

  @action AddRow = row => {
    this.selectedRows.add(row);
  };

  @action RemoveRow = row => {
    this.selectedRows.delete(row);
  };

  @action loadNext = () => {
    // do something with cursors?
    this.requestState = requestState.LOADING_NEW_PAGE;
    const params = this.getConfigureParams(routerStore.route.params);
    params.p = this.pageNumber + 1;
    routerStore.router.navigate(routerStore.route.name, params);
    this.loadPage();
  };

  @action loadPrevious = () => {
    this.requestState = requestState.LOADING_NEW_PAGE;
    const params = this.getConfigureParams(routerStore.route.params);
    const newPageNo = this.pageNumber - 1;
    if (newPageNo > 1) {
      params.p = newPageNo;
    } else {
      delete params.p;
    }
    routerStore.router.navigate(routerStore.route.name, params);
    this.loadPage();
  };

  @action loadPage = () => {
    const onComplete = () => {
      this.requestState = requestState.LOADING_NEW_PAGE;
    };
    this.query.refetchQuery(onComplete);
  };

  @action reConfigure = (doNotQuery = false) => {
    /* when something like filters or orders changing, we are reconfiguring the connection, and will start from Page 0 again */
    const params = this.getConfigureParams(routerStore.route.params);
    delete params.p;
    routerStore.router.navigate(routerStore.route.name, params);
    if (!doNotQuery && this.validRouteName === routerStore.route.name) {
      this.requestState = requestState.RECONFIGURING;
      this.loadPage();
    }
  };

  process = queryResult => {
    const Model = this.constructor.ElementModel;
    if (queryResult !== null) {
      // eslint-disable-next-line no-param-reassign
      queryResult.nodes = queryResult.nodes.map(node => Model.Load(node));
    }
    return queryResult;
  };

  @action SetPage = res => {
    if (res !== null) {
      // we cannot rely on setpage getting called on refetch as if Apoll returns the same result (with different variables) - it won't fire callbacks
      // this.requestState = requestState.LOADING_NEW_PAGE;
      this.nodes = res.nodes;
      this.total = res.total;
    }
  };

  getConfigureParams = paramsMap => {
    /* Take a map retrived from the router and return an object useable as 'params' to navigate using the router
    Should this be made generic in core/routing? What about the array normalisation we are doing here? */
    const params = { ...paramsMap };
    const { sort } = paramsMap;
    if (sort && sort !== this.constructor.defaultSort) {
      params.sort = sort;
    }
    const { order } = paramsMap;
    if (order && order !== this.constructor.defaultOrder) {
      params.order = order;
    }
    const { filters } = paramsMap;
    if (filters) {
      // Make filters conveniently editable
      // TODO: isn't this a long way to get to paramsMap.get('filters')?
      // But it is normalised to an array by appliedFilterInputs!
      // AND urlParamToFilter does uniqify entries!
      params.filters = this.filtersToParams(this.appliedFilterInputs);
    }
    return params;
  };

  urlParamToFilter = filter => {
    const [fName, op, ...val] = filter.split('-');

    const valJoined = val.join('-');
    if (!fName || !op || !valJoined) {
      throw Error('Malformed filter parameter');
    }
    const filterColumn = this.filterColumns.find(f => {
      if (f.filterParamName === fName || f.accessor === fName) {
        return true;
      }
      return false;
    });
    return new FilterInput({
      field: fName,
      value: valJoined,
      operator: op.toUpperCase(),
      type: filterColumn.fieldType,
    });
  };

  filtersToParams(filterObjs) {
    return new Array(
      ...new Set(
        filterObjs.map(filter => {
          return `${filter.field}-${
            filter.operator
          }-${filter.value.toString()}`;
        }),
      ),
    );
  }

  @computed.struct get filterParams() {
    /* stop filters from re-firing when previous and next values are structurally equal */
    return routerStore.params.get('filters');
  }

  @computed get appliedFilterInputs() {
    // appliedFilterInputs
    /* Normalise the filterParams (from routerStore) into an array and convert url params to FilterInput Object */
    const urlFilters = this.filterParams;
    if (urlFilters) {
      if (Array.isArray(urlFilters)) {
        return urlFilters.map(this.urlParamToFilter);
      }
      // if there is only a single ?filter={filterString}, we don't get an array
      return [this.urlParamToFilter(urlFilters)];
    }
    return [];
  }

  @computed get appliedFilters() {
    const appliedFilters = {};
    this.appliedFilterInputs.forEach(fltInput => {
      if (!appliedFilters[fltInput.field]) {
        appliedFilters[fltInput.field] = [fltInput];
      } else {
        appliedFilters[fltInput.field].push(fltInput);
      }
    });
    return appliedFilters;
  }

  @computed get localFilters() {
    return Object.values(this.filters).filter(filt => filt.filterField.applied);
  }

  @action disposeFilters = () => {
    Object.values(this.filters).forEach(filt => filt.dispose?.());
  };

  @action InitialiseFilters = () => {
    /* Create Filters and FilterFields from columns
    Read from routerStore to create Filters to create 'selected' state AND set FilterInputs on FilterField. 
    We may re-initialise filters when for example we switch portal. In this case we must dispose existing Filters
    since in some cases (e.g. space.js) these Filters have reactions. This brings up questions about how the lifecycle of Filters 
    should ideally be structured. */

    // dispose existing filters
    this.disposeFilters();
    // construct new filters
    this.filters = {};
    this.filterColumns.forEach(col => {
      const Filter = col.filter;
      const { filterArgs = {} } = col;
      const fieldName = col.filterParamName || col.accessor;
      const colFilterInputs = this.appliedFilters[fieldName] || [];
      const fil = new Filter({
        fieldName,
        fieldTitle: (col.Header && col.Header) || col.accessor,
        valuesEnum: filterArgs.valuesEnum,
        selected: colFilterInputs.length !== 0,
        filterInputs: colFilterInputs,
      });
      this.filters[fieldName] = fil;
    });
  };

  @action ClearFilters = () => {
    this.selectedFilters.forEach(filter => {
      filter.filterField.ToggleSelect();
      if (filter.SetTouched) filter.SetTouched(false);
      if (filter.SetApplying) filter.SetApplying(false);
      // We don't want to lose the state of the filter just not submit it as part of the filter state
      // filter.filterField.filterInputs = [];
    });
  };

  @computed get selectedFilters() {
    return Object.values(this.filters).filter(filter => {
      return filter.filterField.selected;
    });
  }

  @action ApplyFilters = setOpen => {
    const filterInputs = [];
    let valid = true;
    this.selectedFilters.forEach(filter => {
      if (!filter.isValid) {
        valid = false;
      }
      filterInputs.push(...filter.filterField.filterInputs);
    });
    if (valid) {
      // Deselect filter when there are no inputs
      this.selectedFilters
        .filter(filter => filter.filterField.filterInputs.length === 0)
        .forEach(filter => {
          filter.filterField.SetSelected(false);
        });
      // let each filter know it is being applied
      Object.values(this.filters).forEach(fl => {
        if (fl.OnApplyingFilter) {
          fl.OnApplyingFilter();
        }
      });

      const params = this.getConfigureParams(routerStore.route.params);
      params.filters = this.filtersToParams(filterInputs);
      routerStore.router.navigate(routerStore.route.name, params);
      if (setOpen) setOpen(false);
      // Note: 'SetApplying' is used for filter validation
      this.selectedFilters.forEach(filter => {
        if (filter.SetApplying) filter.SetApplying(false);
      });
    } else {
      this.selectedFilters.forEach(filter => {
        if (filter.SetApplying) filter.SetApplying(true);
      });
    }
  };

  @computed get filtersApiInput() {
    const applied = this.appliedFilters;
    const filterElements = [];
    // eslint-disable-next-line no-restricted-syntax
    for (const key of Object.keys(applied)) {
      const filterInputs = applied[key];
      if (!this.filters[key]) {
        // Filters haven't been initialised. If we are here, we initialised the filters too late.
        // Or filters are misconfigured. Let's assume we haven't yet initialised.
        break;
      }
      const opJoin = this.filters[key].constructor.OpJoin;
      if (opJoin === 'OR') {
        // join 'OR'  -- We must assume that all filterInputs for a field have the same operator
        // i.e. limited graphql API. Probably only matters for relatively advanced use-cases
        filterElements.push({
          field: key,
          operator: filterInputs[0].operator,
          values: filterInputs.map(flterInput => flterInput.value.toJSON()),
        });
      } else if (['AND', undefined].includes(opJoin)) {
        filterElements.push(
          ...filterInputs.map(flterInput => {
            return {
              field: flterInput.field,
              values: [flterInput.value.toJSON()],
              operator: flterInput.operator,
            };
          }),
        );
      }
    }
    return filterElements;
  }

  @action SetSortField = accessor => {
    const params = this.getConfigureParams(routerStore.route.params);
    if (accessor !== this.constructor.defaultSort) {
      params.sort = accessor;
    } else {
      delete params.sort;
    }
    routerStore.router.navigate(routerStore.route.name, params);
  };

  @action SetSortOrder = order => {
    const params = this.getConfigureParams(routerStore.route.params);
    if (order !== this.constructor.defaultOrder) {
      params.order = order;
    } else {
      delete params.order;
    }
    routerStore.router.navigate(routerStore.route.name, params);
  };
}
